import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { MdSearch, MdInfoOutline, MdLocationOn, MdElectricBolt, MdPerson, MdAssignment } from "react-icons/md";

export default function FeederInfo() {
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formik = useFormik({
    initialValues: { account: "" },
    validationSchema: Yup.object({
      account: Yup.string()
        .required("Account or meter number is required")
        .min(6, "Invalid account number"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      setCustomerData(null);

      const endpoint = "https://bedcbilldownload.smartpowerbilling.com/api/customer/";
      const username = "cussuite";
      const password = "Cussuite@123";
      const authHeader = "Basic " + btoa(`${username}:${password}`);

      try {
        const response = await axios({
          method: "POST",
          url: endpoint,
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
          },
          data: { acc_no: values.account },
        });
        if (response.data && response.data.result) {
          setCustomerData(response.data.result.customer);
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || "Check your account number and try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const FieldRow = ({ icon: Icon, label, value }) => (
    <div className="flex flex-col sm:flex-row border-b border-gray-100 last:border-none hover:bg-green-50/30 transition-all duration-300">
      <div className="flex items-center gap-3 px-6 py-4 sm:w-1/3 font-bold text-gray-500 text-xs uppercase tracking-wider bg-gray-50/50 sm:bg-transparent">
        {Icon && <Icon className="text-green-700" size={16} />}
        {label}
      </div>
      <div className="px-6 py-4 sm:w-2/3 text-black break-words font-semibold text-xs sm:text-sm md:text-base uppercase tracking-tight">
        {value || "—"}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col font-sans">
      <div className="bg-[#14532d] border-b border-gray-100 py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight mb-4">
            Customer <span className="text-yellow-400">Feeder Info</span>
          </h1>
          <div className="w-20 h-1.5 bg-yellow-400 mx-auto rounded-full mb-6"></div>
          <p className="text-sm sm:text-base text-white opacity-90 font-normal">Verify your supply details and service band instantly.</p>
        </div>
      </div>

      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">

          {/* SEARCH CARD */}
          <div className="bg-black rounded-2xl p-1 shadow-xl mb-8">
            <div className="bg-white rounded-xl p-6 sm:p-8">
              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-grow group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdSearch className="h-5 w-5 text-green-700 group-focus-within:text-yellow-500 transition-colors" />
                    </div>
                    <input
                      type="text"
                      name="account"
                      placeholder="Account or Meter Number..."
                      value={formik.values.account}
                      onChange={formik.handleChange}
                      className={`block w-full pl-12 pr-4 py-3.5 bg-gray-50 text-sm sm:text-base font-bold border-2 rounded-xl focus:ring-4 focus:ring-green-100 focus:bg-white outline-none transition-all ${formik.touched.account && formik.errors.account ? "border-red-400 bg-red-50" : "border-gray-100 focus:border-green-700"
                        }`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-green-700 text-white px-8 py-3.5 rounded-xl font-bold uppercase text-xs tracking-wider hover:bg-black active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-md"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>Search <span className="hidden sm:inline">Details</span></>
                    )}
                  </button>
                </div>

                {formik.touched.account && formik.errors.account && (
                  <p className="text-xs text-red-600 font-bold flex items-center gap-1.5 animate-pulse">
                    <MdInfoOutline /> {formik.errors.account}
                  </p>
                )}
              </form>

              {error && (
                <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-xl text-red-700 text-xs font-bold flex items-center gap-2.5">
                  <MdInfoOutline size={20} /> {error}
                </div>
              )}
            </div>
          </div>

          {/* RESULTS AREA */}
          <div className="min-h-[350px]">
            {!customerData && !loading && !error && (
              <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400">
                <MdAssignment size={64} className="mb-4 opacity-30" />
                <p className="font-bold text-xs uppercase tracking-wider">Search result will appear here</p>
              </div>
            )}

            {loading && (
              <div className="flex flex-col items-center justify-center py-16 space-y-4">
                <div className="w-12 h-12 border-4 border-green-700 border-t-yellow-400 rounded-full animate-spin"></div>
                <p className="text-green-700 font-bold text-xs uppercase tracking-wider animate-pulse">Retrieving information...</p>
              </div>
            )}

            {customerData && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-500">
                <div className="bg-black px-6 py-6 flex justify-between items-center">
                  <h3 className="text-white font-bold uppercase tracking-tight flex items-center gap-2.5 text-base sm:text-lg">
                    <MdElectricBolt className="text-yellow-400" size={24} />
                    Customer Profile
                  </h3>
                  <span className="bg-green-700 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">Verified Info</span>
                </div>

                <div className="divide-y divide-gray-100">
                  <FieldRow icon={MdPerson} label="Account Name" value={customerData.name} />
                  <FieldRow icon={MdLocationOn} label="Service Address" value={customerData.address} />
                  <FieldRow label="Transformer" value={customerData.transformer} />
                  <FieldRow label="Service Band" value={customerData.band} />
                  <FieldRow label="Tariff Class" value={customerData.tariff} />
                  <FieldRow label="33KV Feeder" value={customerData.feeder33} />
                  <FieldRow label="11KV Feeder" value={customerData.feeder11} />
                </div>

                <div className="bg-gray-50 p-6 text-center border-t border-gray-100">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Need help with this information?</p>
                  <a href="/contact" className="inline-block bg-yellow-400 text-black px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-black hover:text-white transition-all shadow-md">
                    Contact Support
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}